import toast from 'react-hot-toast';
import type { UseFormRegister } from 'react-hook-form';
import {
  HEADER_MEDIA_BY_FORMAT,
  MIN_SCHEDULE_MINUTES,
} from '../constants/whatsapp';
import type { CampaignFormValues, WhatsappTemplate } from '../types/whatsapp';
import { earliestSendTime } from '../utils/sendTime';

const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500';
const labelClass = 'mb-1 block text-sm font-medium text-gray-700';
const errorClass = 'mt-1 text-sm text-red-600';
const hintClass = 'mt-1 text-xs text-gray-500';

interface CampaignFieldsProps {
  templates: WhatsappTemplate[];
  template: WhatsappTemplate | null;
  register: UseFormRegister<CampaignFormValues>;
  errors: Partial<Record<keyof CampaignFormValues, { message?: string }>>;
  mediaFile: File | null;
  onMediaChange: (file: File | null) => void;
}

export default function CampaignFields({
  templates,
  template,
  register,
  errors,
  mediaFile,
  onMediaChange,
}: CampaignFieldsProps) {
  const mediaRules = template?.header_format
    ? HEADER_MEDIA_BY_FORMAT[
        template.header_format as keyof typeof HEADER_MEDIA_BY_FORMAT
      ]
    : undefined;
  const mediaProblem = (picked: File) => {
    if (!mediaRules) return null;
    if (!mediaRules.mimeTypes.includes(picked.type as never))
      return mediaRules.wrongTypeMessage;
    if (picked.size > mediaRules.maxBytes) return mediaRules.tooBigMessage;

    return null;
  };

  const chooseMedia = (input: HTMLInputElement) => {
    const picked = input.files?.[0] ?? null;
    if (!picked || !mediaRules) {
      onMediaChange(null);
      return;
    }

    const problem = mediaProblem(picked);
    if (problem) {
      // Clear both. Leave the state and the previous picture is still the one
      // that gets sent; leave the input and it goes on showing a file name we
      // refused. Either on its own reads as though the new file was accepted.
      input.value = '';
      onMediaChange(null);
      toast.error(problem);
      return;
    }

    onMediaChange(picked);
  };

  const hasUrlButton = template?.buttons.some((one) => one.sub_type === 'url');
  const hasCopyCode = template?.buttons.some(
    (one) => one.sub_type === 'copy_code',
  );

  return (
    <>
      <div>
        <label htmlFor="campaign_name" className={labelClass}>
          Comms name
        </label>
        <input
          id="campaign_name"
          type="text"
          maxLength={120}
          placeholder="August member update"
          {...register('campaign_name', {
            required: 'Name this comms first.',
          })}
          className={inputClass}
        />
        {errors.campaign_name && (
          <p className={errorClass}>{errors.campaign_name.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="template_meta_id" className={labelClass}>
          Template
        </label>
        <select
          id="template_meta_id"
          {...register('template_meta_id', {
            required: 'Pick a template first.',
          })}
          className={`${inputClass} bg-white`}
        >
          <option value="">Choose a template</option>
          {/* The backend leaves out anything it cannot send, so every
              template that reaches here is one we can offer. */}
          {templates.map((one) => (
            <option key={one.meta_id} value={one.meta_id}>
              {one.name} ({one.language})
            </option>
          ))}
        </select>
        {errors.template_meta_id && (
          <p className={errorClass}>{errors.template_meta_id.message}</p>
        )}
      </div>

      {mediaRules && (
        <div>
          <label htmlFor="header_media" className={labelClass}>
            {mediaRules.label}
          </label>
          <input
            id="header_media"
            type="file"
            accept={mediaRules.mimeTypes.join(',')}
            onChange={(event) => chooseMedia(event.target)}
            className="w-full text-sm text-gray-700 file:mr-3 file:rounded-md file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-gray-50"
          />
          {!mediaFile && (
            <p className={hintClass}>{mediaRules.missingMessage}</p>
          )}
        </div>
      )}

      {hasUrlButton && (
        <div>
          <label htmlFor="button_suffix" className={labelClass}>
            Button link
          </label>
          <input
            id="button_suffix"
            type="text"
            placeholder="events/convention-2026"
            {...register('button_suffix', {
              required: 'This template needs a link value.',
            })}
            className={inputClass}
          />
          {errors.button_suffix && (
            <p className={errorClass}>{errors.button_suffix.message}</p>
          )}
          <p className={hintClass}>
            Just the part after the domain. Everyone gets the same link.
          </p>
        </div>
      )}

      {hasCopyCode && (
        <div>
          <label htmlFor="coupon_code" className={labelClass}>
            Offer code
          </label>
          <input
            id="coupon_code"
            type="text"
            maxLength={15}
            {...register('coupon_code', {
              required: 'This template needs an offer code.',
            })}
            className={inputClass}
          />
          {errors.coupon_code && (
            <p className={errorClass}>{errors.coupon_code.message}</p>
          )}
        </div>
      )}

      <div>
        <label htmlFor="send_at" className={labelClass}>
          Send at (SAST)
        </label>
        <input
          id="send_at"
          type="datetime-local"
          min={earliestSendTime()}
          {...register('send_at')}
          className={inputClass}
        />
        <p className={hintClass}>
          Leave this empty to send straight away. A scheduled time must be at
          least {MIN_SCHEDULE_MINUTES} minutes from now.
        </p>
      </div>
    </>
  );
}
